/**
 * api.ts — the HTTP client for the Lineage backend. Field lists are NEVER
 * hard-coded: forms are generated from the `inputSchema` returned by
 * `GET /api/contracts`. A tiny pub/sub (`onDataChanged`) lets pages refetch the
 * workspace after any command writes.
 */
import type { Workspace } from "../../store/events.ts";
import type { DomainEvent } from "../../store/events.ts";
import type { ContractMeta } from "../../cqrs/contract.ts";
import type { MethodPack } from "../../domain/method-packs.ts";
import type { PrimitiveDefinition } from "../../domain/primitives.ts";
import type { PipelineStage } from "../../domain/pipeline.ts";

export type { ContractMeta };

export interface JsonSchema {
  type?: string | string[];
  properties?: Record<string, JsonSchema>;
  required?: readonly string[];
  items?: JsonSchema;
  enum?: readonly (string | number | boolean | null)[];
  const?: unknown;
  anyOf?: readonly JsonSchema[];
  description?: string;
  title?: string;
  default?: unknown;
  format?: string;
  minLength?: number;
  maxLength?: number;
  [key: string]: unknown;
}

export interface RegistryPayload {
  primitives: readonly PrimitiveDefinition[];
  packs: readonly MethodPack[];
  pipeline: readonly PipelineStage[];
  boundaryLadder: readonly string[];
  evaluationDimensions: readonly string[];
  provider: { id: string; model: string; synthetic: boolean };
}

export type Ack = { ok: boolean; message?: string; goto?: string; value?: unknown };
export type CommandResult =
  | { kind: "ok"; value: Ack }
  | { kind: "approval_required"; contract: string; hint: string }
  | { kind: "error"; message: string };

/**
 * When OIDC auth is enabled server-side, an unauthenticated API call returns 401
 * with `{ loginUrl }`. Bounce the whole document to Keycloak (full navigation,
 * not fetch — the OIDC redirect must happen at the top level). Returns true when
 * it handled a 401 so callers can stop. A no-op when auth is disabled (no 401s).
 */
function redirectIfUnauthenticated(res: Response): boolean {
  if (res.status !== 401) return false;
  const here = window.location.pathname + window.location.search + window.location.hash;
  window.location.href = `/auth/login?returnTo=${encodeURIComponent(here)}`;
  return true;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (redirectIfUnauthenticated(res)) return new Promise<T>(() => {}); // never resolves; navigation in flight
  if (!res.ok) throw new Error(`${path}: ${res.status}`);
  return (await res.json()) as T;
}

export const listContracts = (): Promise<readonly ContractMeta[]> =>
  getJson<{ contracts: readonly ContractMeta[] }>("/api/contracts").then((b) => b.contracts);
export const getRegistry = (): Promise<RegistryPayload> => getJson<RegistryPayload>("/api/registry");
export const getState = (): Promise<Workspace> => getJson<Workspace>("/api/state");
export const getEvents = (): Promise<readonly DomainEvent[]> =>
  getJson<{ events: readonly DomainEvent[] }>("/api/events").then((b) => b.events);

export async function runCommand(name: string, input: unknown, approvalGranted = false): Promise<CommandResult> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (approvalGranted) headers["x-lineage-approval"] = "granted";
  const res = await fetch(`/api/commands/${encodeURIComponent(name)}`, {
    method: "POST",
    headers,
    body: JSON.stringify(input ?? {}),
  });
  if (res.ok) {
    const value = (await res.json()) as Ack;
    if (value.ok) bumpData();
    return { kind: "ok", value };
  }
  if (redirectIfUnauthenticated(res)) return new Promise<CommandResult>(() => {});
  const body = (await res.json().catch(() => ({}))) as { error?: string; contract?: string; hint?: string; message?: string };
  if (res.status === 409 && body.error === "approval_required") {
    return { kind: "approval_required", contract: body.contract ?? name, hint: body.hint ?? "" };
  }
  return { kind: "error", message: body.message ?? body.error ?? `HTTP ${res.status}` };
}

export async function runQuery<T = unknown>(name: string, input: unknown): Promise<T> {
  const res = await fetch(`/api/queries/${encodeURIComponent(name)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input ?? {}),
  });
  if (redirectIfUnauthenticated(res)) return new Promise<T>(() => {});
  if (!res.ok) throw new Error(`query ${name}: ${res.status}`);
  return (await res.json()) as T;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

/** Every SSE event the chat agent can emit (mirrors `api/agent.ts` AgentEvent). */
export type AgentEvent =
  | { type: "RUN_STARTED"; runId: string; provider: string; model: string }
  | { type: "TEXT_MESSAGE_CONTENT"; runId: string; delta: string }
  | { type: "TOOL_CALL"; name: string; args: unknown }
  | { type: "TOOL_RESULT"; name: string; ok: boolean; summary: string }
  | { type: "UI_NAVIGATE"; path: string }
  | { type: "DATA_CHANGED" }
  | { type: "RUN_FINISHED"; runId: string }
  | { type: "RUN_CANCELED"; runId: string }
  | { type: "RUN_ERROR"; runId: string; message: string };

export interface ChatRequest {
  messages: ChatMessage[];
  locale?: string;
  route?: string;
}

/** Record a human edit to one turn of an Assistant run (provenance only — see api/agent.ts).
 * Returns true when the server recorded it; the workspace refetches via bumpData. */
export async function editTurn(input: {
  runId: string;
  turn: "prompt" | "response";
  revised: string;
  disclosure: "full" | "final-only" | "flagged";
  intent?: string;
}): Promise<boolean> {
  const res = await fetch("/api/agent/edit", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  }).catch(() => null);
  if (res && res.ok) {
    bumpData();
    return true;
  }
  return false;
}

/** Co-edit the shared GenUI scratchpad (add/update/remove/move/clear). Bumps data on success. */
export async function scratchpadOp(body: {
  op: "add" | "update" | "remove" | "move" | "clear";
  padId?: string;
  blockId?: string;
  block?: unknown;
  toIndex?: number;
}): Promise<boolean> {
  const res = await fetch("/api/genui/op", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => null);
  if (res && res.ok) {
    bumpData();
    return true;
  }
  return false;
}

/** Ask the server to cancel an in-flight Assistant run (cooperative; best-effort). */
export async function cancelAgent(runId: string): Promise<void> {
  await fetch("/api/agent/cancel", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ runId }),
  }).catch(() => undefined);
}

/** Stream a chat turn over SSE, dispatching each parsed event. Resolves on finish. */
export async function streamAgent(req: ChatRequest, onEvent: (event: AgentEvent) => void): Promise<void> {
  const res = await fetch("/api/agent", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.body) throw new Error("no agent stream");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl = buffer.indexOf("\n\n");
    while (nl >= 0) {
      const frame = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 2);
      if (frame.startsWith("data:")) {
        try {
          onEvent(JSON.parse(frame.slice(5).trim()) as AgentEvent);
        } catch {
          /* ignore malformed frame */
        }
      }
      nl = buffer.indexOf("\n\n");
    }
  }
}

// ── tiny data-change bus ───────────────────────────────────────────────────────
const dataListeners = new Set<() => void>();
let dataVersion = 0;
export function bumpData(): void {
  dataVersion += 1;
  for (const fn of dataListeners) fn();
}
export function onDataChanged(fn: () => void): () => void {
  dataListeners.add(fn);
  return () => dataListeners.delete(fn);
}
export const currentDataVersion = (): number => dataVersion;
